/**
 * Activity storage service — CRUD for Activity entities in the graph model.
 *
 * Handles recorded outings with time fields and links to Plans/Routes.
 */

import { db } from '../database/schema.js'
import type { Activity } from '../schemas/activity.js'
import type { ActivityType } from '../schemas/activity.js'
import { EntityStorageService } from './entity-storage.js'
import { validateActivity, runValidation } from './validation.js'
import type { StorageResult } from './base-storage.js'
import { edgeService, nodeRef } from '../services/edge-service.js'
import type { Origin } from '../schemas/origin.js'

export interface CreateActivityData {
  name?: string | undefined
  description?: string | undefined
  activityType: ActivityType
  startTime?: string | undefined
  endTime?: string | undefined
  conditions?: string | undefined
  origin: Origin
  createdBy: string
}

class ActivityStorageService extends EntityStorageService<Activity> {
  protected readonly nodeType = 'activity'

  constructor() {
    super(db.activities, 'activity')
  }

  async createActivity(data: CreateActivityData): Promise<StorageResult<Activity>> {
    const activityData = {
      name: data.name,
      description: data.description,
      activityType: data.activityType,
      startTime: data.startTime,
      endTime: data.endTime,
      conditions: data.conditions,
      origin: data.origin,
      createdBy: data.createdBy,
    }

    const validation = runValidation(validateActivity, activityData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.createWithLayerGroup(
      activityData as Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>
    )
  }

  async updateActivity(id: string, updates: Partial<Activity>): Promise<StorageResult<Activity>> {
    const validation = runValidation(validateActivity, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }
    return this.update(id, updates)
  }

  async deleteActivity(id: string): Promise<StorageResult<void>> {
    return this.deleteWithEdges(id)
  }

  async getActivitiesForUser(_userId: string): Promise<StorageResult<Activity[]>> {
    return this.executeOperation(async () => {
      const all = await this.table.toArray()
      return all.filter(a => (a as Record<string, unknown>).accessStatus !== 'revoked')
    }, 'get activities for user')
  }

  /** Link this activity to a plan */
  async linkToPlan(activityId: string, planId: string): Promise<void> {
    const actRef = nodeRef('activity', activityId)
    const planRef = nodeRef('plan', planId)

    // activity → linked-to-plan → plan
    const exists1 = await edgeService.exists(actRef, 'linked-to-plan', planRef)
    if (!exists1) {
      await edgeService.create({
        subject: actRef,
        predicate: 'linked-to-plan',
        object: planRef,
      })
    }

    // plan → has-activity → activity (reverse edge for easy lookup)
    const exists2 = await edgeService.exists(planRef, 'has-activity', actRef)
    if (!exists2) {
      await edgeService.create({
        subject: planRef,
        predicate: 'has-activity',
        object: actRef,
      })
    }
  }

  /** Unlink this activity from a plan */
  async unlinkFromPlan(activityId: string, planId: string): Promise<void> {
    await edgeService.deleteExact(
      nodeRef('activity', activityId),
      'linked-to-plan',
      nodeRef('plan', planId)
    )
    await edgeService.deleteExact(
      nodeRef('plan', planId),
      'has-activity',
      nodeRef('activity', activityId)
    )
  }

  /** Link this activity to a route it followed */
  async linkToRoute(activityId: string, routeId: string): Promise<void> {
    const exists = await edgeService.exists(
      nodeRef('activity', activityId),
      'follows-route',
      nodeRef('route', routeId)
    )
    if (!exists) {
      await edgeService.create({
        subject: nodeRef('activity', activityId),
        predicate: 'follows-route',
        object: nodeRef('route', routeId),
      })
    }
  }

  /** Get the plan ID this activity is linked to (if any) */
  async getLinkedPlanId(activityId: string): Promise<string | undefined> {
    const ids = await edgeService.getObjectIds(nodeRef('activity', activityId), 'linked-to-plan')
    return ids[0]
  }

  /** Get the route ID this activity followed (if any) */
  async getFollowedRouteId(activityId: string): Promise<string | undefined> {
    const ids = await edgeService.getObjectIds(nodeRef('activity', activityId), 'follows-route')
    return ids[0]
  }
}

export const activityStorage = new ActivityStorageService()
